/**
 * @fileoverview The message when exact copies were skipped: "Already added, 1 copy skipped" (the count for
 * several), with Undo, which adds them after all as their own photos (B61). One message for a whole batch. An Undo that
 * fails is reported and said by the customer error standard (lib/serverFailure).
 *
 * Where: every screen that adds photos (the Photos tray, a plant's or a bed's Add a photo, a seed-packet scan, the
 * late Google Photos hand-over).
 * Words (en/es): photoCopies.skippedOne, photoCopies.skipped, photoCopies.undo.
 * Settings: none (the toast's own time).
 *
 * @module photoCopies
 */

import { toast } from '@/shared/components/ui/use-toast';
import { ToastAction } from '@/shared/components/ui/toast';
import { t } from '@/shared/i18n/i18nSetup';
import { serverFailureText, serverFailureWords, reportOtherFailure } from './serverFailure';
import { errorMessage } from './errorMessage';
import { skippedKey, undoAll, type PhotoCopySkipped } from './photoCopiesCore';

/**
 * Tells the gardener a skipped copy could not be added by Undo, by the customer error standard: reported to Sentry,
 * the standard sentence (it says it was reported and gives the support address; never "try again").
 *
 * @param error - What the re-send threw.
 */
function sayUndoFailed(error: unknown): void {
  // A failure on our side is reported by serverFailureText; anything else (a bug) is reported here.
  const ours = serverFailureText(error, 'Photo copy Undo');
  if (ours === null) reportOtherFailure(error, 'Photo copy Undo');
  toast({ title: ours ?? errorMessage(error, serverFailureWords(error)), variant: 'destructive' });
}

/**
 * Says that exact copies were skipped, with Undo. Nothing is said for none.
 *
 * @param skipped - The copies the server skipped.
 * @param onUndone - Told with the photos stored by Undo, so the screen can show them.
 */
export function sayCopiesSkipped<T>(skipped: PhotoCopySkipped<T>[], onUndone?: (stored: T[]) => void | Promise<void>): void {
  if (skipped.length === 0) return;
  toast({
    title: t(skippedKey(skipped.length), { count: skipped.length }),
    action: (
      <ToastAction altText={t('photoCopies.undo')} onClick={() => { void undoAll(skipped, sayUndoFailed).then((stored) => onUndone?.(stored)); }}>
        {t('photoCopies.undo')}
      </ToastAction>
    ),
  });
}
